import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ITwoFactorSetup } from '@shared/interfaces/twoFactorSetup';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-two-factor-config-modal',
  templateUrl: './two-factor-config-modal.component.html',
  styleUrls: ['./two-factor-config-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorConfigModalComponent implements OnInit, OnDestroy {
  authCode: string = '';
  twoFactorSetup$!: Observable<ITwoFactorSetup>;
  private subscriptions = new Subscription();

  constructor(
    protected dialogRef: NbDialogRef<TwoFactorConfigModalComponent>,
    private api: ApiService,
    private alertService: AlertService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.twoFactorSetup$ = this.api.setupTwoFactorAuth();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  copyCode() {
    this.alertService.success('common.code-copied', 5000);
  }

  getCodeGroups(code: string): string[] {
    return code.replace(/\s/g, '').match(/.{1,4}/g) ?? [];
  }

  error: string | null = null;
  isCodeValid: boolean = false;
  isLoading: boolean = false;

  allowOnlyDigits(event: KeyboardEvent) {
    const char = event.key;

    if (!/^\d$/.test(char)) {
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();

    const pasted = event.clipboardData?.getData('text') || '';
    const digitsOnly = pasted.replace(/\D/g, '').slice(0, 6);

    this.authCode = digitsOnly;
    this.validateCode();
  }

  onCodeChange(value: string) {
    const cleaned = value.replace(/\D/g, '');

    if (value !== cleaned) {
      this.error = 'Only digits are allowed';
    }

    this.authCode = cleaned.slice(0, 6);
    this.validateCode();
  }

  validateCode() {
    this.isCodeValid = this.authCode.length === 6;

    if (this.isCodeValid && this.error === 'Only digits are allowed') {
      this.error = null;
    }

    if (this.error && this.authCode.length < 6) {
      this.error = null;
    }
  }

  verifyCode() {
    if (!this.isCodeValid) return;

    this.isLoading = true;
    this.error = null;

    this.subscriptions.add(
      this.api.verifyTwoFactorAuth(this.authCode).subscribe({
        next: (v) => {
          this.alertService.success(
            'user.two-factor.configuration-success',
            5000
          );
          this.cancel();
        },
        error: (e) => {
          this.isLoading = false;
          this.error = this.translate.instant('user.two-factor.code-invalid');
          this.changeDetectorRef.markForCheck();
        },
      })
    );
  }
}
